import { apiRequest } from '@/shared/api/client';
import type { Bilingual, Envelope, Locale } from '@/shared/types/api';
import type { SessionPayload } from '@/shared/types/domain';
import type { PendingSessionPayload } from '@/shared/types/mfa';
import type { OrganisationChoicePayload } from './organisationChoice';

/**
 * What sign-in and `/me` answer: the complete session, or — while the MFA step
 * is pending — only `mfa` and the pending identity (MFA contract).
 */
export type SessionResponse = SessionPayload | PendingSessionPayload;

/**
 * What a sign-in step answers: a session as above, or — when the address and
 * password open more than one organisation — the choice that is step 2. A
 * choice opens no session (auth contract §1).
 */
export type SignInResponse = SessionResponse | OrganisationChoicePayload;

/**
 * Step 1: the address and the password, and nothing else. The organisation
 * is never typed: when the address belongs to several, the server offers the
 * ones this password opens.
 */
export interface LoginInput {
  email: string;
  password: string;
}

export async function login(input: LoginInput): Promise<SignInResponse> {
  const response = await apiRequest<Envelope<SignInResponse>>('/auth/login', {
    method: 'POST',
    body: { email: input.email, password: input.password },
  });

  return response.data;
}

/** Step 2: the organisation chosen, against the challenge step 1 issued. */
export interface OrganisationChoiceInput {
  selection_token: string;
  /** The organisation's public id, as offered. */
  organisation: string;
}

export async function chooseOrganisation(input: OrganisationChoiceInput): Promise<SignInResponse> {
  const response = await apiRequest<Envelope<SignInResponse>>('/auth/login/organisation', {
    method: 'POST',
    body: { selection_token: input.selection_token, organisation: input.organisation },
  });

  return response.data;
}

export async function logout(): Promise<void> {
  await apiRequest<void>('/auth/logout', { method: 'POST' });
}

export async function fetchSession(): Promise<SessionResponse> {
  const response = await apiRequest<Envelope<SessionResponse>>('/me');

  return response.data;
}

/**
 * The invitation behind an activation link.
 *
 * Enough to let the invited person recognise the account before they commit a
 * password to it — whose organisation it belongs to and which address it is
 * for — and nothing else. Reading it needs no session: the token is the
 * authority.
 */
export interface ActivationInvitation {
  email: string;
  name: string;
  organisation: { name: Bilingual; default_locale: Locale };
  expires_at: string;
}

export interface ActivationInput {
  password: string;
  password_confirmation: string;
}

export interface ActivationResult {
  email: string;
  status: string;
}

/**
 * A token that never existed, has expired, was revoked or has already been
 * spent all answer 404 `INVITATION_INVALID`. That is deliberate on the server's
 * side and the client must not try to undo it — see `ActivationPage`.
 */
export async function fetchInvitation(token: string): Promise<ActivationInvitation> {
  const response = await apiRequest<Envelope<ActivationInvitation>>(
    `/activation/${encodeURIComponent(token)}`,
  );

  return response.data;
}

export async function activateAccount(
  token: string,
  input: ActivationInput,
): Promise<ActivationResult> {
  const response = await apiRequest<Envelope<ActivationResult>>(
    `/activation/${encodeURIComponent(token)}`,
    { method: 'POST', body: input },
  );

  return response.data;
}
